import * as moment from 'moment';
import { IWatrRequest, IWatrRequestTravels } from '@nkr/models/index';
import { Row, Col } from 'react-bootstrap';
import { Label } from 'react-bootstrap';
import { WageAndTravelReimbursementRequestStatus } from '@nkr/enums/index';
import { normalizeEmploymentType } from './utils/employment-type.utils';
import styled from 'styled-components';

const InfoHeader = styled.div<{ $compact?: boolean }>`
    background: white;
    border-radius: 6px;
    padding: ${p => p.$compact ? '8px 10px' : '12px 16px'};
    margin-bottom: ${p => p.$compact ? '8px' : '16px'};
    border: 1px solid #e1e8ed;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

const InfoItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const InfoLabel = styled.span`
    font-size: 11px;
    color: #212529;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;

const InfoValue = styled.span`
    font-size: 14px;
    color: #6c757d;
    font-weight: 600;
`;

const DonorIdentityBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

const DonorDisplayName = styled.span`
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
    line-height: 1.25;
    word-break: break-word;
`;

const DonorDisplayAlias = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    line-height: 1.3;
`;

interface IProps {
    data: IWatrRequest
    travel?: IWatrRequestTravels,
    showStatus: boolean
    showEmploymentType?: boolean
    compact?: boolean
}

const getEmploymentTypeDisplay = (employmentType: any): string => {
    if (!employmentType && employmentType !== 0) return '-';
    
    const normalized = normalizeEmploymentType(employmentType);
    
    if (normalized === undefined) {
        const type = typeof employmentType === 'string' 
            ? employmentType.toUpperCase().trim() 
            : String(employmentType).toUpperCase().trim();
        return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase().replace(/_/g, '-');
    }
    
    switch (normalized) {
        case 'HOURLY':
            return 'Hourly';
        case 'SALARIED':
            return 'Salaried';
        case 'SELF_EMPLOYED':
            return 'Self-Employed';
        default:
            return '-';
    }
};

const Header = (props: IProps) => {
    const donorName = [props.data.firstName, props.data.lastName].filter(Boolean).join(' ').trim() || '—';
    const donorAlias = (props.data.alias || '').trim() || '—';
    const donationDateDisplay = moment(props.data.donationDate).isValid()
        ? moment(props.data.donationDate).format('MM/DD/YYYY')
        : (props.data.donationDate || '—');

    return (
        <InfoHeader $compact={props.compact}>
            <Row>
                <Col md={3} sm={6} xs={12}>
                    <InfoItem>
                        <InfoLabel>Donor Identity</InfoLabel>
                        <DonorIdentityBlock>
                            <DonorDisplayName title={donorName}>{donorName}</DonorDisplayName>
                            <DonorDisplayAlias title={donorAlias}>{donorAlias}</DonorDisplayAlias>
                        </DonorIdentityBlock>
                    </InfoItem>
                </Col>
                <Col md={2} sm={6} xs={6}>
                    <InfoItem>
                        <InfoLabel>Submission Date</InfoLabel>
                        <InfoValue>{props.travel ? moment(props.travel.createdDateTime).format('MM/DD/YYYY') : moment(props.data.createdDate).format('MM/DD/YYYY')}</InfoValue>
                    </InfoItem>
                </Col>
                <Col md={2} sm={6} xs={6}>
                    <InfoItem>
                        <InfoLabel>Center</InfoLabel>
                        <InfoValue>{props.data.centerAlias}</InfoValue>
                    </InfoItem>
                </Col>
                <Col md={2} sm={6} xs={6}>
                    <InfoItem>
                        <InfoLabel>Donation Date</InfoLabel>
                        <InfoValue>{donationDateDisplay}</InfoValue>
                    </InfoItem>
                </Col>
                {props.showEmploymentType !== false && (
                    <Col md={3} sm={6} xs={6}>
                        <InfoItem>
                            <InfoLabel>Employment Type</InfoLabel>
                            <InfoValue>{getEmploymentTypeDisplay(props.data.employmentType)}</InfoValue>
                        </InfoItem>
                    </Col>
                )}
            </Row>
        </InfoHeader>
    );
}

export default Header;